import { useState, useCallback, useMemo } from 'react';
import { Platform } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { AuthAPI } from '@/services/residents/api/AuthAPI';
import { useCreateAccountMutation } from '@/store/api';
import { AuthStackParamList } from '@/navigation/residents/mobile/AuthNavigator';
import { isEmailValid, isNameValid, validateSignupForm } from '@/utils/residents/validation';

type SignUpNavigationProp = StackNavigationProp<AuthStackParamList>;

export interface UseSignUpFormReturn {
  name: string;
  setName: (name: string) => void;
  email: string;
  setEmail: (email: string) => void;
  userType: 'resident' | 'guarantor' | null;
  setUserType: (type: 'resident' | 'guarantor') => void;
  isLoading: boolean;
  canSubmit: boolean;
  submit: () => void;
  formError: string | null;
  // Platform-specific properties
  isMobile: boolean;
  isWeb: boolean;
}

export const useSignUpForm = (): UseSignUpFormReturn => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [userType, setUserType] = useState<'resident' | 'guarantor' | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  
  const navigation = useNavigation<SignUpNavigationProp>();
  
  // RTK Query hook for create account
  const [createAccountMutation, { isLoading: createAccountLoading }] = useCreateAccountMutation();
  
  // Combined loading state
  const combinedIsLoading = isLoading || createAccountLoading;
  
  // Platform detection
  const isMobile = Platform.OS !== 'web';
  const isWeb = Platform.OS === 'web';

  // Form validation - different logic for mobile vs web
  const canSubmit = useMemo(() => {
    if (isMobile) {
      // Mobile (iOS-matching): Only need name and email (user type selected in previous screen)
      return isEmailValid(email) && isNameValid(name) && !combinedIsLoading;
    } else {
      // Web: Need name, email, and user type selection
      return isEmailValid(email) && isNameValid(name) && userType !== null && !combinedIsLoading;
    }
  }, [email, name, userType, combinedIsLoading, isMobile]);

  // Clear error when user starts typing
  const handleSetName = useCallback((newName: string) => {
    setName(newName);
    if (formError) {
      setFormError(null);
    }
  }, [formError]);

  const handleSetEmail = useCallback((newEmail: string) => {
    setEmail(newEmail);
    if (formError) {
      setFormError(null);
    }
  }, [formError]);

  const handleSetUserType = useCallback((type: 'resident' | 'guarantor') => {
    setUserType(type);
    if (formError) {
      setFormError(null);
    }
  }, [formError]);

  // Submit function
  const submit = useCallback(async () => {
    if (!canSubmit) return;

    // Validate fields before submission
    const validationError = validateSignupForm(name, email);
    if (validationError) {
      setFormError(validationError);
      return;
    }

    setIsLoading(true);
    setFormError(null);

    try {
      let result;
      
      if (isMobile) {
        // Mobile (iOS-matching): Use createAccount with hardcoded type: 1 (resident)
        // This matches iOS behavior where user type is selected in UserSelectionView
        result = await createAccountMutation({
          email: email.trim(),
          type: 1, // resident
        }).unwrap();
      } else {
        // Web (web apps-matching): Use createAccount with user type selection
        // type: 1 = resident, type: 2 = guarantor/parent
        const accountType = userType === 'resident' ? 1 : 2;
        result = await createAccountMutation({
          email: email.trim(),
          type: accountType,
        }).unwrap();
      }

      if (result.success) {
        // Navigate to account verification screen like iOS does
        navigation.navigate('AccountVerification', { 
          email: email.trim(),
          isSignup: true
        });
      } else {
        setFormError(result.message || 'Unable to sign up with the provided email');
      }
    } catch (error: any) {
      console.error('Signup error:', error);
      
      // Handle specific error cases
      if (error?.message?.includes('already')) {
        setFormError('There is already an account registered with that email address.');
      } else if (error?.message?.includes('network') || error?.message?.includes('Network')) {
        setFormError('Network error. Please check your connection and try again.');
      } else {
        setFormError(error?.message || 'Unable to sign up with the provided email');
      }
    } finally {
      setIsLoading(false);
    }
  }, [canSubmit, name, email, userType, navigation]);

  return {
    name,
    setName: handleSetName,
    email,
    setEmail: handleSetEmail,
    userType,
    setUserType: handleSetUserType,
    isLoading: combinedIsLoading,
    canSubmit,
    submit,
    formError,
    isMobile,
    isWeb,
  };
};
